<script lang="ts">
  import Flash, { type FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { normalizeStringWithFolding } from '#app/lib/strings'
  import InputWithClearButton from '#components/input_with_clear_button.svelte'
  import Modal from '#components/modal.svelte'
  import Spinner from '#components/spinner.svelte'
  import type { OnSelectShelf } from '#inventory/types/inventory_browser'
  import ShelfEditor from '#shelves/components/shelf_editor.svelte'
  import ShelfLi from '#shelves/components/shelf_li.svelte'
  import { shelfMatchesFilter, type SerializedShelf } from '#shelves/lib/shelves'
  import { I18n } from '#user/lib/i18n'
  import type { SerializedUser } from '#users/lib/users'

  const {
    waitForShelves,
    shelves,
    user,
    onSelectShelf,
  }: {
    waitForShelves: Promise<void>
    shelves: SerializedShelf[]
    user: SerializedUser
    onSelectShelf?: OnSelectShelf
  } = $props()

  const isMainUser = $derived(user.isMainUser)

  let flash: FlashState

  let shelvesFilter: string = $state('')
  const normalizedShelvesFilter = $derived(normalizeStringWithFolding(shelvesFilter))
  const filteredDisplayedShelvesIds = $derived.by(() => {
    const ids = shelves
      .filter(shelf => shelfMatchesFilter(shelf, normalizedShelvesFilter))
      .map(shelf => shelf._id)
    return new Set(ids)
  })

  let showShelfCreator: boolean = $state()

  function onShelfEditorDone (newShelf: SerializedShelf) {
    showShelfCreator = false
    shelves.push(newShelf)
    onSelectShelf(newShelf)
  }
</script>

<div class="shelves-section">
  {#await waitForShelves}
    <div class="header">
      <h3 class="subheader">{I18n('shelves')} <Spinner /></h3>
    </div>
  {:then}
    {#if shelves.length > 0}
      <div class="header">
        <h3 class="subheader">{I18n('shelves')}</h3>
        <div class="right">
          {#if shelves.length > 5}
            <InputWithClearButton
              bind:value={shelvesFilter}
              placeholder="Filter shelves…"
              count={filteredDisplayedShelvesIds.size}
              total={shelves.length}
            />
          {/if}
          {#if isMainUser}
            <button
              class="action tiny-button light-blue"
              aria-haspopup="dialog"
              onclick={() => showShelfCreator = true}
            >
              {@html icon('plus')}{I18n('create shelf')}
            </button>
          {/if}
        </div>
      </div>
    {/if}
  {/await}

  <div id="shelves-list">
    {#if shelves?.length > 0}
      <ul>
        {#each shelves as shelf (shelf._id)}
          {#if filteredDisplayedShelvesIds.has(shelf._id)}
            <ShelfLi {shelf} {onSelectShelf} />
          {/if}
        {/each}
        {#if isMainUser && !shelvesFilter}
          <ShelfLi withoutShelf={true} {onSelectShelf} />
        {/if}
      </ul>
    {/if}
  </div>
</div>

<Flash state={flash} />

{#if showShelfCreator}
  <Modal onCloseModal={() => showShelfCreator = false}>
    <ShelfEditor {onShelfEditorDone} />
  </Modal>
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .shelves-section{
    background-color: $light-grey;
  }
  .header{
    margin-block-start: 0.5em;
    @include display-flex(row, center, space-between, wrap);
    padding: 0.8em 1em;
    button:not(:last-child){
      margin-inline-end: 0.5em;
    }
  }
  .subheader{
    margin-block: 0;
    font-size: 1rem;
    @include sans-serif;
    flex: 1 0 auto;
    padding-inline-end: 0.8em;
    @include display-flex(row, center, space-between);
  }
  .right{
    @include display-flex(row, center, flex-end);
  }
  /* Smaller screens */
  @media screen and (width < $smaller-screen){
    .header, .right{
      @include display-flex(column, center, center);
    }
    .right{
      :global(.input-with-clear-button){
        max-width: min(90vw, 20rem);
        margin: 0.5rem;
      }
      button{
        margin: 0.5rem;
      }
    }
  }
  /* Larger screens */
  @media screen and (width >= $smaller-screen){
    .right{
      :global(.input-with-clear-button){
        margin-inline-end: 1rem;
      }
    }
  }
  #shelves-list ul{
    padding: 0 0.5rem 0.5rem;
    max-height: min(60vh, 15rem);
    overflow-y: auto;
    /* Large screens */
    @media screen and (width >= $smaller-screen){
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(25em, 1fr));
      gap: 0.5rem;
    }
    /* Small screens */
    @media screen and (width < $smaller-screen){
      :global(li){
        margin-block-end: 0.5rem;
      }
    }
  }
</style>
